import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Loader2, Save, Send, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parseDays = (text) =>
  String(text || "")
    .split(/[,\s]+/)
    .map((d) => parseInt(d, 10))
    .filter((d) => !Number.isNaN(d) && d >= 0 && d <= 30);

/** Game-day reminders for league fixtures: lead times in days, per league. */
export const LeagueGameReminders = () => {
  const [cfg, setCfg] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [leagueRows, setLeagueRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState("");
  const [tpl, setTpl] = useState(null);
  const [registering, setRegistering] = useState(false);

  const loadTemplate = async () => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/league-reminders/template`,
        { withCredentials: true });
      setTpl(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the template status");
    }
  };

  const registerTemplate = async () => {
    setRegistering(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/league-reminders/template`, {},
        { withCredentials: true });
      toast.success(r.data.already
        ? `Already submitted · ${r.data.status}`
        : `Sent to WhatsApp for approval · ${r.data.content_sid}`);
      await loadTemplate();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Twilio refused the template");
    } finally {
      setRegistering(false);
    }
  };

  const load = async () => {
    try {
      const [s, sch] = await Promise.all([
        axios.get(`${API}/admin/whatsapp/settings`, { withCredentials: true }),
        axios.get(`${API}/admin/whatsapp/league-reminders/schedule`, { withCredentials: true }),
      ]);
      const block = s.data.settings?.league_game_reminder || s.data.league_game_reminder || {};
      setCfg({
        enabled: !!block.enabled,
        default_days_before: (block.default_days_before || [2, 0]).join(", "),
        send_time: block.send_time || "18:00",
        morning_time: block.morning_time || "07:30",
        template: block.template || "",
        content_sid: block.content_sid || "",
      });
      setLeagueRows(Object.entries(block.leagues || {}).map(([league, v]) => ({
        league, days: (v.days_before || []).join(", "),
      })));
      setSchedule(sch.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load game reminders");
    }
  };

  useEffect(() => { load(); loadTemplate(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const leagues = {};
      leagueRows.forEach((r) => {
        const name = (r.league || "").trim();
        if (name) leagues[name] = { days_before: parseDays(r.days) };
      });
      await axios.put(`${API}/admin/whatsapp/settings`, {
        league_game_reminder: {
          enabled: cfg.enabled,
          default_days_before: parseDays(cfg.default_days_before),
          send_time: cfg.send_time,
          morning_time: cfg.morning_time,
          template: cfg.template,
          content_sid: cfg.content_sid.trim(),
          leagues,
          recipients: "squad_else_all",
        },
      }, { withCredentials: true });
      toast.success("Game reminders saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async (fixtureId) => {
    setSending(fixtureId);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/league-reminders/run`,
        { fixture_id: fixtureId }, { withCredentials: true });
      if (r.data.skipped) {
        toast.error(r.data.reason === "whatsapp_disabled"
          ? "Turn WhatsApp on in Credentials first" : "Reminders are switched off");
      } else {
        toast.success(`Sent ${r.data.total_sent} reminder(s)`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending("");
    }
  };

  if (!cfg) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="league-reminders-card">
      <Card className="p-6 bg-slate-900/50">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BellRing className="w-5 h-5 text-amber-400" /> League game reminders
            </h3>
            <p className="text-sm text-slate-400 mt-1">
              Goes to the match day squad if you&apos;ve picked one, otherwise the whole league
              side. Parents can reply YES or NO and it records their availability.
            </p>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
            data-testid="league-reminders-toggle" />
        </div>

        <div className="grid gap-3 sm:grid-cols-3 mt-5 max-w-2xl">
          <div>
            <Label className="text-white text-sm">Days before (0 = game morning)</Label>
            <Input value={cfg.default_days_before}
              onChange={(e) => setCfg({ ...cfg, default_days_before: e.target.value })}
              placeholder="2, 0" className="mt-1 bg-slate-800 border-slate-700 text-white"
              data-testid="league-reminders-days" />
          </div>
          <div>
            <Label className="text-white text-sm">Advance reminder at</Label>
            <Input type="time" value={cfg.send_time}
              onChange={(e) => setCfg({ ...cfg, send_time: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700 text-white"
              data-testid="league-reminders-send-time" />
          </div>
          <div>
            <Label className="text-white text-sm">Game-morning reminder at</Label>
            <Input type="time" value={cfg.morning_time}
              onChange={(e) => setCfg({ ...cfg, morning_time: e.target.value })}
              className="mt-1 bg-slate-800 border-slate-700 text-white"
              data-testid="league-reminders-morning-time" />
          </div>
        </div>

        <div className="mt-5 max-w-2xl">
          <Label className="text-white text-sm">Per league (overrides the days above)</Label>
          <div className="space-y-2 mt-2">
            {leagueRows.map((row, i) => (
              <div key={i} className="flex gap-2">
                <Input value={row.league} placeholder="JSSL"
                  onChange={(e) => setLeagueRows(leagueRows.map((r, j) =>
                    j === i ? { ...r, league: e.target.value } : r))}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid={`league-row-name-${i}`} />
                <Input value={row.days} placeholder="3, 1, 0"
                  onChange={(e) => setLeagueRows(leagueRows.map((r, j) =>
                    j === i ? { ...r, days: e.target.value } : r))}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid={`league-row-days-${i}`} />
                <Button variant="ghost" className="text-slate-400"
                  onClick={() => setLeagueRows(leagueRows.filter((_, j) => j !== i))}
                  data-testid={`league-row-remove-${i}`}>Remove</Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-200"
              onClick={() => setLeagueRows([...leagueRows, { league: "", days: "" }])}
              data-testid="league-row-add">Add a league</Button>
          </div>
        </div>

        <div className="mt-5 max-w-2xl">
          <Label className="text-white text-sm">Message</Label>
          <textarea value={cfg.template} rows={3}
            onChange={(e) => setCfg({ ...cfg, template: e.target.value })}
            className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 text-white rounded-md text-sm"
            data-testid="league-reminders-template" />
          <p className="text-[11px] text-slate-500 mt-1">
            Use {"{parent_name} {player_name} {team_name} {opponent} {league_name} {fixture_date} "}
            {"{kickoff_block} {meet_block} {venue_block}"}
          </p>
        </div>

        <div className="mt-5 max-w-2xl">
          <Label className="text-white text-sm">WhatsApp approved template</Label>
          <p className="text-[11px] text-amber-300/80 mt-1 mb-2">
            WhatsApp only allows free text within 24 hours of a parent&apos;s last message. For a
            reminder sent days ahead, register this once — the portal submits it to WhatsApp through
            Twilio and starts using it as soon as it&apos;s approved.
          </p>
          <pre className="text-[11px] text-slate-300 bg-slate-950 border border-slate-800 rounded p-3 whitespace-pre-wrap"
            data-testid="league-template-body">
{tpl?.body || "Loading…"}
          </pre>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Button size="sm" onClick={registerTemplate} disabled={registering}
              className="h-9 bg-sky-600 hover:bg-sky-500 text-[11px]"
              data-testid="league-template-register">
              {registering ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              {tpl?.registered ? "Submit a new version" : "Register with WhatsApp"}
            </Button>
            {tpl?.registered && (
              <>
                <Button size="sm" variant="ghost" className="h-9 text-slate-300 text-[11px]"
                  onClick={loadTemplate} data-testid="league-template-refresh">
                  Check approval
                </Button>
                <Badge className={`text-[10px] ${
                  tpl.status === "approved" ? "bg-emerald-500/20 text-emerald-300"
                  : tpl.status === "rejected" ? "bg-red-500/20 text-red-300"
                  : "bg-amber-500/20 text-amber-300"}`} data-testid="league-template-status">
                  {tpl.status || "pending"}
                </Badge>
                <span className="text-[11px] font-mono text-slate-500">{tpl.content_sid}</span>
              </>
            )}
          </div>
          {tpl?.rejection_reason && (
            <p className="text-[11px] text-red-300 mt-1">WhatsApp said: {tpl.rejection_reason}</p>
          )}
          {tpl?.check_error && (
            <p className="text-[11px] text-amber-300 mt-1">{tpl.check_error}</p>
          )}
        </div>

        <div className="mt-5 max-w-2xl">
          <Label className="text-white text-sm">Twilio approved template SID (optional)</Label>
          <Input value={cfg.content_sid} placeholder="HX..."
            onChange={(e) => setCfg({ ...cfg, content_sid: e.target.value })}
            className="mt-1 bg-slate-800 border-slate-700 text-white font-mono"
            data-testid="league-reminders-content-sid" />
          <p className="text-[11px] text-amber-300/80 mt-1">
            Filled in automatically when you register the template above. Paste a SID here only if
            you built the template directly in Twilio. Variables go in
            order: parent name, player, team, opponent, date, kick-off, venue.
          </p>
        </div>

        <Button onClick={save} disabled={saving} className="mt-5 bg-emerald-600 hover:bg-emerald-700"
          data-testid="league-reminders-save">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
      </Card>

      <Card className="p-6 bg-slate-900/50">
        <h3 className="text-base font-semibold text-white mb-3">What&apos;s queued</h3>
        {!schedule?.fixtures?.length && (
          <p className="text-sm text-slate-500" data-testid="league-reminders-empty">
            No upcoming league fixtures yet — add or import them on the League fixtures page.
          </p>
        )}
        <div className="space-y-2">
          {(schedule?.fixtures || []).map((f) => (
            <div key={f.fixture_id} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3"
              data-testid={`league-queue-${f.fixture_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-white">
                  {f.team_name} vs {f.opponent}
                </span>
                <Badge className="bg-slate-700/50 text-slate-300 text-[10px]">
                  {f.date}{f.start_time ? ` ${f.start_time}` : ""}
                </Badge>
                <Badge className="bg-sky-500/20 text-sky-300 text-[10px]">
                  {f.recipient_count} on {f.recipients}
                </Badge>
                <Button size="sm" variant="outline" className="ml-auto h-8 text-[11px] border-slate-700"
                  onClick={() => sendNow(f.fixture_id)} disabled={sending === f.fixture_id}
                  data-testid={`league-send-now-${f.fixture_id}`}>
                  {sending === f.fixture_id
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <><Send className="w-3 h-3 mr-1" /> Send now</>}
                </Button>
              </div>
              <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-400">
                {f.reminders.length === 0 && <span>No lead times set</span>}
                {f.reminders.map((r) => (
                  <span key={r.days_before}>
                    {r.days_before === 0 ? "game morning" : `${r.days_before} day(s) before`}:{" "}
                    {r.already_sent
                      ? <span className="text-emerald-400">sent</span>
                      : (r.goes_out || "").replace("T", " ").slice(0, 16)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
